const environment = {
  defaultApiBaseUrl: "https://auth.autharmor.com"
};

/**
 * A base64 coder that uses the browser's built-in btoa and atob functions.
 */
class BrowserBase64Coder {
  /**
   * @inheritdoc
   */
  encodeToBase64(input) {
    return window.btoa(input);
  }

  /**
   * @inheritdoc
   */
  decodeFromBase64(input) {
    return window.atob(input);
  }

  /**
   * @inheritdoc
   */
  tryDecodeFromBase64(input) {
    try {
      return this.decodeFromBase64(input);
    } catch {
      return null;
    }
  }
}

/**
 * Provides the current date and time using the native Date class.
 */
class NativeSystemClock {
  /**
   * @inheritdoc
   */
  now() {
    return new Date();
  }
}

/**
 * A class that implements the ISha256StringHasher interface using the browser crypto APIs.
 */
class BrowserSha256StringHasher {
  constructor(textEncoder = new TextEncoder()) {
    this.textEncoder = textEncoder;
  }

  /**
   * @inheritdoc
   */
  async hashStringToBuffer(stringInput) {
    const data = this.textEncoder.encode(stringInput);
    const hash = await window.crypto.subtle.digest("SHA-256", data);
    return hash;
  }

  /**
   * @inheritdoc
   */
  async hashStringToHexString(stringInput) {
    const hash = await this.hashStringToBuffer(stringInput);
    const hashArray = Array.from(new Uint8Array(hash));
    const hashHexString = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
    return hashHexString;
  }

  /**
   * @inheritdoc
   */
  async generateHmac(secret, stringInput) {
    const data = this.textEncoder.encode(stringInput);
    const key = await window.crypto.subtle.importKey("raw", secret, {
      name: "HMAC",
      hash: {
        name: "SHA-256"
      }
    }, false, ["sign", "verify"]);
    const hash = await window.crypto.subtle.sign("HMAC", key, data);
    const hashArray = Array.from(new Uint8Array(hash));
    const hashHexString = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
    return hashHexString;
  }
}

/**
 * Generates a nonce using the browser's crypto API.
 */
class BrowserNonceGenerator {
  /**
   * Generates a nonce.
   *
   * @returns The nonce.
   */
  generateNonce() {
    return window.crypto.randomUUID().replace("-", "");
  }
}

/**
 * A hostname service that uses the browser's current hostname.
 */
class BrowserHostNameService {
  /**
   * @inheritdoc
   */
  getHostName() {
    return window.location.hostname;
  }
}

/**
 * Implements the request signing logic for the AuthArmor API.
 */
class AuthArmorApiRequestSigner {
  /**
   * The signing key used to sign requests to the AuthArmor API.
   */

  /**
   * @param clientSdkApiKey The client SDK API key.
   * @param systemClock The system clock implementation.
   * @param nonceGenerator The nonce generator implementation.
   * @param base64Coder The base64 coder implementation.
   * @param sha256StringHasher The SHA256 string hasher implementation.
   */
  constructor(clientSdkApiKey, systemClock = new NativeSystemClock(), nonceGenerator = new BrowserNonceGenerator(), base64Coder = new BrowserBase64Coder(), sha256StringHasher = new BrowserSha256StringHasher(), hostNameService = new BrowserHostNameService()) {
    this.systemClock = systemClock;
    this.nonceGenerator = nonceGenerator;
    this.base64Coder = base64Coder;
    this.sha256StringHasher = sha256StringHasher;
    this.hostNameService = hostNameService;
    this.signingKey = this.getSigningKeyFromApiKey(clientSdkApiKey);
  }

  /**
   * Signs a request to the AuthArmor API.
   *
   * @param url The URL of the request.
   * @param body The body of the request.
   *
   * @returns The signature of the request.
   */
  async signRequest(url, body) {
    const timestamp = this.systemClock.now().toISOString();
    const nonce = this.nonceGenerator.generateNonce();
    const urlObject = new URL(url);
    const path = urlObject.pathname.endsWith("/") ? urlObject.pathname.slice(0, -1) : urlObject.pathname;
    const host = this.hostNameService.getHostName();
    const signaturePayload = [this.signingKey, timestamp, path, host].join("|");
    const signatureHash = await this.sha256StringHasher.hashStringToBuffer(signaturePayload);
    const bodyHash = await this.sha256StringHasher.hashStringToHexString(body);
    const userAgent = navigator.userAgent.trim().toLowerCase();
    const message = [bodyHash, userAgent, timestamp, nonce].join("|");
    const signature = await this.sha256StringHasher.generateHmac(signatureHash, message);
    const signatureWithMetadata = [signature, timestamp, nonce].join("|");
    return signatureWithMetadata;
  }

  /**
   * Gets the request signing key from the API key JWT.
   *
   * @param apiKey The API key JWT.
   * @returns The signing key.
   */
  getSigningKeyFromApiKey(apiKey) {
    // The API key is a JWT (JSON Web Token) that contains the signing key in the payload.
    // We extract it from the 'key' property of the payload.

    const apiKeyJwtComponents = apiKey.split(".");
    if (apiKeyJwtComponents.length !== 3) {
      throw new Error("Invalid API key.");
    }
    const keyPayloadJsonBase64 = apiKeyJwtComponents[1];
    const keyPayloadJson = this.base64Coder.decodeFromBase64(keyPayloadJsonBase64);
    const keyPayload = JSON.parse(keyPayloadJson);
    const signingKey = keyPayload["key"];
    return signingKey;
  }
}

class ApiError extends Error {
  constructor(rawError) {
    super(`[${rawError.error_code}] ${rawError.error_message}`);
    this.rawError = rawError;
    this.statusCode = rawError.error_code;
    this.trackingId = rawError.tracking_id;
  }
}

/**
 * A low-level client for the AuthArmor JS SDK API that exposes its endpoints.
 */
class AuthArmorApiClient {
  constructor(configuration, requestSigner = new AuthArmorApiRequestSigner(configuration.clientSdkApiKey)) {
    this.requestSigner = requestSigner;
    this.clientSdkApiKey = configuration.clientSdkApiKey;
    this.apiBaseUrl = configuration.baseUrl ?? environment.defaultApiBaseUrl;
  }

  /**
   * Retrieves the configuration for the SDK.
   */
  async getSdkConfigurationAsync({} = {}) {
    return await this.fetchAsync("/api/v4/config/sdkinit");
  }

  /**
   * Retrieves the authentication methods the user is enrolled for.
   *
   * @returns The user's enrolled authentication methods.
   */
  async getUserEnrollmentsAsync({
    username
  }) {
    const userId = "00000000-0000-0000-0000-000000000000";
    return await this.fetchAsync(`/api/v4/users/${userId}/enrolledmethods`, "post", {
      username_or_email_address: username
    });
  }

  /**
   * Gets the status of an authentication session.
   *
   * @returns The status of the authentication session.
   *
   * @remarks This endpoint should be polled when the user authenticates with a method that requires polling.
   */
  async getAuthenticationSessionStatusAsync({
    sessionId
  }) {
    return await this.fetchAsync(`/api/v4/auth/request/status/${sessionId}`);
  }

  /**
   * Gets the status of a registration session.
   *
   * @returns The status of the registration session.
   *
   * @remarks This endpoint should be polled when the user registers with a method that requires polling.
   */
  async getRegistrationSessionStatusAsync({
    registrationId
  }) {
    return await this.fetchAsync(`/api/v4/users/registration/${registrationId}`);
  }

  /**
   * Starts an authentication session for a specific user using the AuthArmor Authenticator.
   *
   * @returns The authentication session.
   *
   * @remarks
   * Poll the status of the session using the `getAuthenticationSessionStatus` method. The
   * ReCaptcha action is `auth`.
   */
  async startAuthenticatorUserSpecificAuthenticationAsync({
    username,
    sendPushNotification,
    useVisualVerify,
    actionName,
    shortMessage,
    originLocation,
    timeoutSeconds,
    nonce
  }, captchaConfirmation) {
    return await this.fetchAsync("/api/v4/auth/request/authenticator/start", "post", {
      username,
      send_push: sendPushNotification,
      use_visual_verify: useVisualVerify,
      origin_location_data: originLocation,
      action_name: actionName,
      short_msg: shortMessage,
      timeout_in_seconds: timeoutSeconds,
      hCaptcha_token: captchaConfirmation?.hCaptchaResponse,
      nonce
    });
  }

  /**
   * Starts a usernameless authentication session using the AuthArmor Authenticator with a QR code to be scanned.
   *
   * @returns The authentication session.
   *
   * @remarks Poll the status of the session using the `getAuthenticationSessionStatus` method.
   */
  async startAuthenticatorUsernamelessAuthenticationAsync({
    useVisualVerify,
    actionName,
    shortMessage,
    originLocation,
    timeoutSeconds,
    nonce
  }) {
    return await this.fetchAsync("/api/v4/auth/request/authenticator/start", "post", {
      send_push: false,
      use_visual_verify: useVisualVerify,
      origin_location_data: originLocation,
      action_name: actionName,
      short_msg: shortMessage,
      timeout_in_seconds: timeoutSeconds,
      nonce
    });
  }

  /**
   * Starts a WebAuthn authentication session.
   *
   * @returns The authentication session.
   *
   * @remarks
   * After performing WebAuthn authentication on the session, call the
   * `completeWebAuthnAuthentication` method with the result.
   */
  async startWebAuthnAuthenticationAsync({
    username,
    attachmentType,
    webAuthnClientId,
    nonce
  }) {
    return await this.fetchAsync("/api/v4/auth/request/webauthn/start", "post", {
      username,
      attachment_type: attachmentType,
      webauthn_client_id: webAuthnClientId,
      nonce
    });
  }

  /**
   * Completes a WebAuthn authentication session.
   *
   * @returns The authentication result.
   */
  async completeWebAuthnAuthenticationAsync({
    authenticatorResponseData,
    authRequestId,
    authArmorSignature,
    webAuthnClientId
  }) {
    return await this.fetchAsync("/api/v4/auth/request/webauthn/finish", "post", {
      authenticator_response_data: authenticatorResponseData,
      auth_request_id: authRequestId,
      aa_sig: authArmorSignature,
      webauthn_client_id: webAuthnClientId
    });
  }

  /**
   * Sends a magic link to the user's email address to log in.
   *
   * @remarks
   * The user will be redirected to the URL specified in the `redirectUrl` parameter, which
   * must be registered via the AuthArmor dashboard. The resulting session will be authenticated.
   */
  async sendMagicLinkEmailForAuthenticationAsync({
    username,
    redirectUrl,
    context,
    actionName,
    shortMessage,
    originLocation,
    timeoutSeconds,
    nonce
  }, captchaConfirmation) {
    return await this.fetchAsync("/api/v4/auth/request/magiclink/start", "post", {
      username,
      authentication_redirect_url: redirectUrl,
      context_data: context,
      origin_location_data: originLocation,
      action_name: actionName,
      short_msg: shortMessage,
      timeout_in_seconds: timeoutSeconds,
      hCaptcha_token: captchaConfirmation?.hCaptchaResponse,
      nonce
    });
  }

  /**
   * Starts a registration session using the AuthArmor Authenticator.
   *
   * @returns The authentication session.
   */
  async startAuthenticatorRegistrationAsync({
    username,
    actionName,
    shortMessage,
    originLocation,
    timeoutSeconds,
    nonce
  }) {
    return await this.fetchAsync("/api/v4/users/register/authenticator/start", "post", {
      username,
      origin_location_data: originLocation,
      action_name: actionName,
      short_msg: shortMessage,
      timeout_in_seconds: timeoutSeconds,
      nonce
    });
  }

  /**
   * Starts a registration session using WebAuthn.
   *
   * @returns The WebAuthn registration session.
   *
   * @remarks
   * After performing WebAuthn authentication on the session, call the
   * `completeWebAuthnRegistration` method with the result.
   */
  async startWebAuthnRegistrationAsync({
    username,
    attachmentType,
    residentKeyRequirementType,
    userVerificationRequirementType,
    webAuthnClientId,
    nonce
  }) {
    return await this.fetchAsync("/api/v4/users/register/webauthn/start", "post", {
      username,
      attachment_type: attachmentType,
      resident_key_requirement_type: residentKeyRequirementType,
      userVerificationRequirementType: userVerificationRequirementType,
      webauthn_client_id: webAuthnClientId,
      nonce
    });
  }

  /**
   * Completes a WebAuthn registration session.
   *
   * @returns The registration result.
   */
  async completeWebAuthnRegistrationAsync({
    authenticatorResponseData,
    registrationId,
    authArmorSignature,
    webAuthnClientId
  }) {
    return await this.fetchAsync("/api/v4/users/register/webauthn/finish", "post", {
      authenticator_response_data: authenticatorResponseData,
      registration_id: registrationId,
      aa_sig: authArmorSignature,
      webauthn_client_id: webAuthnClientId
    });
  }

  /**
   * Sends a magic link to the user's email address to register.
   *
   * @returns The registration session.
   *
   * @remarks
   * The user will be redirected to the URL specified in the `redirectUrl` parameter, which
   * must be registered via the AuthArmor dashboard. The resulting session will be authenticated.
   */
  async sendMagicLinkEmailForRegistrationAsync({
    username,
    redirectUrl,
    context,
    actionName,
    shortMessage,
    originLocation,
    timeoutSeconds,
    nonce
  }, captchaConfirmation) {
    return this.fetchAsync("/api/v4/users/register/magiclink/start", "post", {
      email_address: username,
      registration_redirect_url: redirectUrl,
      context_data: context,
      action_name: actionName,
      short_msg: shortMessage,
      origin_location_data: originLocation,
      timeout_in_seconds: timeoutSeconds,
      hCaptcha_token: captchaConfirmation?.hCaptchaResponse,
      nonce
    });
  }

  /**
   * Fetches a resource from the AuthArmor API.
   *
   * @param relativeUrl The relative URL to fetch.
   * @param method The HTTP method to use.
   * @param payload The payload to send with the request.
   *
   * @returns The response body.
   */
  async fetchAsync(relativeUrl, method = "get", payload, headers) {
    const url = new URL(`${this.apiBaseUrl}${relativeUrl}`);
    url.searchParams.append("apikey", this.clientSdkApiKey);
    const finalUrl = url.toString();
    const encodedPayload = payload !== undefined ? JSON.stringify(payload ?? {}) : null;
    const signature = await this.requestSigner.signRequest(finalUrl, encodedPayload ?? "");
    const finalHeaders = {
      ...headers,
      "X-AuthArmor-ClientMsgSigv1": signature
    };
    if (encodedPayload !== null) {
      finalHeaders["Content-Type"] = "application/json";
    }
    const options = {
      method,
      headers: finalHeaders,
      body: encodedPayload
    };
    const response = await fetch(finalUrl, options);
    if (!response.ok) {
      const errorBody = await response.json();
      throw new ApiError(errorBody);
    }
    const responseBody = await response.json();
    return responseBody;
  }
}

let AuthenticationRequestStatusId = /*#__PURE__*/function (AuthenticationRequestStatusId) {
  AuthenticationRequestStatusId[AuthenticationRequestStatusId["Failed"] = 2] = "Failed";
  AuthenticationRequestStatusId[AuthenticationRequestStatusId["PendingApproval"] = 3] = "PendingApproval";
  AuthenticationRequestStatusId[AuthenticationRequestStatusId["PendingValidation"] = 4] = "PendingValidation";
  return AuthenticationRequestStatusId;
}({});
let AuthenticationRequestCode = /*#__PURE__*/function (AuthenticationRequestCode) {
  AuthenticationRequestCode[AuthenticationRequestCode["Pending"] = 0] = "Pending";
  AuthenticationRequestCode[AuthenticationRequestCode["Declined"] = 3] = "Declined";
  AuthenticationRequestCode[AuthenticationRequestCode["TimedOut"] = 5] = "TimedOut";
  AuthenticationRequestCode[AuthenticationRequestCode["Succeeded"] = 8] = "Succeeded";
  return AuthenticationRequestCode;
}({});

let RegistrationRequestStatusCode = /*#__PURE__*/function (RegistrationRequestStatusCode) {
  RegistrationRequestStatusCode[RegistrationRequestStatusCode["Unknown"] = 0] = "Unknown";
  RegistrationRequestStatusCode[RegistrationRequestStatusCode["PendingUserAcceptance"] = 1] = "PendingUserAcceptance";
  RegistrationRequestStatusCode[RegistrationRequestStatusCode["Registered"] = 2] = "Registered";
  RegistrationRequestStatusCode[RegistrationRequestStatusCode["Declined"] = 3] = "Declined";
  RegistrationRequestStatusCode[RegistrationRequestStatusCode["InvalidSignature"] = 4] = "InvalidSignature";
  RegistrationRequestStatusCode[RegistrationRequestStatusCode["Timeout"] = 5] = "Timeout";
  RegistrationRequestStatusCode[RegistrationRequestStatusCode["AcceptedPendingDeviceEnrollment"] = 6] = "AcceptedPendingDeviceEnrollment";
  RegistrationRequestStatusCode[RegistrationRequestStatusCode["PendingValidation"] = 8] = "PendingValidation";
  RegistrationRequestStatusCode[RegistrationRequestStatusCode["ValidationTimeout"] = 9] = "ValidationTimeout";
  RegistrationRequestStatusCode[RegistrationRequestStatusCode["SecurityException"] = 10] = "SecurityException";
  return RegistrationRequestStatusCode;
}({});

let AuthMethod = /*#__PURE__*/function (AuthMethod) {
  AuthMethod[AuthMethod["Authenticator"] = 4] = "Authenticator";
  AuthMethod[AuthMethod["MagicLinkEmail"] = 20] = "MagicLinkEmail";
  AuthMethod[AuthMethod["Passkey"] = 30] = "Passkey";
  return AuthMethod;
}({});

class WebAuthnRequestDeniedError extends Error {
  constructor() {
    super("Request denied.");
  }
}

class WebAuthnService {
  constructor(webAuthnClientId, base64Coder = new BrowserBase64Coder()) {
    this.webAuthnClientId = webAuthnClientId;
    this.base64Coder = base64Coder;
  }
  async authenticateAsync(authenticateRequest) {
    const jsonOptions = JSON.parse(authenticateRequest.fido2_json_options);
    const publicKey = this.processPublicKeyCredentialRequest(jsonOptions);
    let attestation;
    try {
      attestation = await navigator.credentials.get({
        publicKey
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        attestation = null;
      } else {
        throw error;
      }
    }
    if (attestation === null) {
      throw new WebAuthnRequestDeniedError();
    }
    const result = {
      authenticator_response_data: this.getAuthenticatorResponseData(attestation),
      auth_request_id: authenticateRequest.auth_request_id,
      aa_sig: authenticateRequest.aa_guid,
      webauthn_client_id: this.webAuthnClientId
    };
    return result;
  }
  async registerAsync(registrationRequest) {
    const jsonOptions = JSON.parse(registrationRequest.fido2_json_options);
    const publicKey = this.processPublicKeyCredentialRequest(jsonOptions);
    let attestation;
    try {
      attestation = await navigator.credentials.create({
        publicKey
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        attestation = null;
      } else {
        throw error;
      }
    }
    if (attestation === null) {
      throw new WebAuthnRequestDeniedError();
    }
    const result = {
      authenticator_response_data: this.getAuthenticatorResponseData(attestation),
      registration_id: registrationRequest.registration_id,
      aa_sig: registrationRequest.aa_sig,
      webauthn_client_id: this.webAuthnClientId
    };
    return result;
  }
  getAuthenticatorResponseData(attestation) {
    const attestationResponse = attestation.response;
    const data = {
      id: attestation.id,
      rawId: this.getBase64FromArrayBuffer(attestation.rawId),
      attestation_object: this.getBase64FromArrayBuffer("attestationObject" in attestationResponse ? attestationResponse.attestationObject : attestationResponse.authenticatorData),
      authenticator_data: "authenticatorData" in attestationResponse ? this.getBase64FromArrayBuffer(attestationResponse.authenticatorData) : null,
      client_data: this.getBase64FromArrayBuffer(attestationResponse.clientDataJSON),
      user_handle: "userHandle" in attestationResponse && attestationResponse.userHandle !== null ? this.getBase64FromArrayBuffer(attestationResponse.userHandle) : null,
      extensions: attestation.getClientExtensionResults(),
      signature: "signature" in attestationResponse ? this.getBase64FromArrayBuffer(attestationResponse.signature) : null
    };
    return data;
  }
  processPublicKeyCredentialRequest(publicKeyCredentialRequest) {
    const processedRequest = {
      ...publicKeyCredentialRequest,
      allowCredentials: publicKeyCredentialRequest.allowCredentials?.map(credential => ({
        ...credential,
        id: this.getUint8ArrayFromBase64(credential.id)
      })),
      excludeCredentials: publicKeyCredentialRequest.excludeCredentials?.map(credential => ({
        ...credential,
        id: this.getUint8ArrayFromBase64(credential.id)
      })),
      user: typeof publicKeyCredentialRequest.user === "object" ? {
        ...publicKeyCredentialRequest.user,
        id: this.getUint8ArrayFromBase64(publicKeyCredentialRequest.user.id)
      } : publicKeyCredentialRequest.user,
      challenge: this.getUint8ArrayFromBase64(publicKeyCredentialRequest.challenge)
    };
    return processedRequest;
  }
  getUint8ArrayFromBase64(base64) {
    const normalizedBase64 = this.normalizeBase64(base64);
    const binaryString = this.base64Coder.decodeFromBase64(normalizedBase64);
    const array = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      array[i] = binaryString.charCodeAt(i);
    }
    return array;
  }
  getBase64FromArrayBuffer(buffer) {
    const array = new Uint8Array(buffer);
    let binaryString = "";
    for (const byte of array) {
      binaryString += String.fromCharCode(byte);
    }
    const base64 = this.base64Coder.encodeToBase64(binaryString);
    const denormalizedBase64 = this.denormalizeBase64(base64);
    return denormalizedBase64;
  }
  normalizeBase64(base64) {
    let encoded = base64.replace(/-/g, "+").replace(/_/g, "/");
    while (encoded.length % 4 !== 0) {
      encoded += "=";
    }
    return encoded;
  }
  denormalizeBase64(base64) {
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
  }
}

/**
 * The client for programatically interacting with AuthArmor's client-side SDK.
 */
class AuthArmorClient {
  /**
   * The promise returned by `initializeInternal`.
   */
  initializationPromise = null;

  /**
   * The ReCaptcha site ID.
   */
  hCaptchaSiteId = null;

  /**
   * The WebAuthn client ID.
   */

  /**
   * @param configuration The configuration for the client.
   * @param apiClient The API client for making requests to the AuthArmor API.
   * @param nonceGenerator The nonce generator.
   * @param systemClock The system clock.
   */
  constructor(configuration, apiClient = new AuthArmorApiClient({
    clientSdkApiKey: configuration.clientSdkApiKey
  }), webAuthnService = configuration.webAuthnClientId !== undefined ? new WebAuthnService(configuration.webAuthnClientId) : null, nonceGenerator = new BrowserNonceGenerator(), systemClock = new NativeSystemClock()) {
    this.apiClient = apiClient;
    this.webAuthnService = webAuthnService;
    this.nonceGenerator = nonceGenerator;
    this.systemClock = systemClock;
    this.webAuthnClientId = configuration.webAuthnClientId ?? null;
  }

  /**
   * Ensures that the client has been initialized for making requests.
   *
   * @returns A promise that resolves when the client has been initialized.
   *
   * @remarks
   * This method is called automatically by the client when needed, but may be called manually by
   * the user to control when the initialization occurs.
   */
  async ensureInitialized() {
    if (this.initializationPromise === null) {
      this.initializationPromise = this.initializeInternal();
    }
    await this.initializationPromise;
  }

  /**
   * Retrieves the hCaptcha site ID.
   *
   * @returns A promise that resolves with the hCaptcha site ID, or null if hCaptcha is disabled.
   *
   * @remarks This method only issues a network request if the client is not initialized.
   */
  async getHCaptchaSiteId() {
    await this.ensureInitialized();
    return this.hCaptchaSiteId;
  }

  /**
   * Gets the available authentication methods for a user.
   *
   * @param username The username of the user.
   *
   * @returns A promise that resolves with the available authentication methods.
   */
  async getAvailableAuthenticationMethodsAsync(username) {
    await this.ensureInitialized();
    const userEnrollments = await this.apiClient.getUserEnrollmentsAsync({
      username
    });
    const authMethods = userEnrollments.enrolled_auth_methods.map(m => m.auth_method_id);
    const result = {
      authenticator: authMethods.includes(AuthMethod.Authenticator),
      magicLinkEmail: authMethods.includes(AuthMethod.MagicLinkEmail),
      passkey: authMethods.includes(AuthMethod.Passkey)
    };
    return result;
  }

  /**
   * Authenticates a user using their authenticator app.
   *
   * @param username The username of the user to log in.
   * @param options The options to use for this request.
   * @param captchaConfirmationRequest The CAPTCHA confirmation.
   * @param abortSignal The abort signal to use for this request.
   *
   * @returns A promise that resolves with a QR code result for the authentication result.
   */
  async authenticateWithAuthenticatorAsync(username, {
    sendPushNotification = true,
    useVisualVerify = false,
    actionName = "Log in",
    shortMessage = "Log in pending, please authorize",
    timeoutSeconds = 60
  } = {}, captchaConfirmationRequest, abortSignal) {
    await this.ensureInitialized();
    const nonce = this.nonceGenerator.generateNonce();
    const authSession = await this.apiClient.startAuthenticatorUserSpecificAuthenticationAsync({
      username,
      sendPushNotification,
      useVisualVerify,
      actionName,
      shortMessage,
      timeoutSeconds,
      nonce
    }, captchaConfirmationRequest);
    const result = {
      qrCodeUrl: authSession.qr_code_data,
      verificationCode: authSession.visual_verify_value || null,
      resultAsync: async () => await this.pollForAuthenticationResultAsync("authenticator", authSession.auth_request_id, authSession.auth_validation_token, timeoutSeconds, abortSignal)
    };
    return result;
  }

  /**
   * Authenticates a user using an authenticator QR code that is not user-specific.
   *
   * @param options The options to use for this request.
   * @param abortSignal The abort signal to use for this request.
   *
   * @returns A promise that resolves with a QR code result for the authentication result.
   */
  async authenticateWithAuthenticatorUsernamelessAsync({
    useVisualVerify = false,
    actionName = "Log in",
    shortMessage = "Log in pending, please authorize",
    timeoutSeconds = 60
  } = {}, abortSignal) {
    await this.ensureInitialized();
    const nonce = this.nonceGenerator.generateNonce();
    const authSession = await this.apiClient.startAuthenticatorUsernamelessAuthenticationAsync({
      useVisualVerify,
      actionName,
      shortMessage,
      timeoutSeconds,
      nonce
    });
    const result = {
      qrCodeUrl: authSession.qr_code_data,
      verificationCode: authSession.visual_verify_value || null,
      resultAsync: async () => await this.pollForAuthenticationResultAsync("authenticator", authSession.auth_request_id, authSession.auth_validation_token, timeoutSeconds, abortSignal)
    };
    return result;
  }

  /**
   * Sends an authentication magic link to the user's email address.
   *
   * @param emailAddress The email address of the user.
   * @param redirectUrl The URL to redirect to after the user has logged in.
   * @param captchaConfirmationRequest The CAPTCHA confirmation.
   * @param options The options to use for this request.
   *
   * @returns A promise that resolves with the registration result.
   *
   * @remarks
   * The user will be redirected to the specified URL after they have logged in. The validation
   * token and request ID will be added as query parameters with the names
   * `auth_validation_token` and `auth_request_id` respectively.
   *
   * The validation token will not be included in the returned `AuthenticationResult`.
   */
  async sendAuthenticateMagicLinkEmailAsync(emailAddress, redirectUrl, {
    context = {},
    actionName = "Log in",
    shortMessage = "Log in pending, please authorize",
    timeoutSeconds = 300
  } = {}, captchaConfirmationRequest, abortSignal) {
    await this.ensureInitialized();
    const nonce = this.nonceGenerator.generateNonce();
    const authSession = await this.apiClient.sendMagicLinkEmailForAuthenticationAsync({
      username: emailAddress,
      redirectUrl,
      context,
      actionName,
      shortMessage,
      timeoutSeconds,
      nonce
    }, captchaConfirmationRequest);
    const result = await this.pollForAuthenticationResultAsync("magicLinkEmail", authSession.auth_request_id, "", timeoutSeconds, abortSignal);
    return result;
  }

  /**
   * Authenticates a user using a passkey.
   *
   * @param username The username of the user.
   *
   * @returns A promise that resolves with the authentication result.
   */
  async authenticateWithPasskeyAsync(username) {
    if (this.webAuthnClientId === null) {
      throw new Error("This AuthArmorClient was not instantiated with passkey support because a WebAuthn client ID was not provided.");
    }
    await this.ensureInitialized();
    const nonce = this.nonceGenerator.generateNonce();
    const authSession = await this.apiClient.startWebAuthnAuthenticationAsync({
      username,
      webAuthnClientId: this.webAuthnClientId,
      attachmentType: "Any",
      nonce
    });
    let authentication;
    try {
      authentication = await this.webAuthnService.authenticateAsync(authSession);
    } catch (error) {
      if (error instanceof WebAuthnRequestDeniedError) {
        const result = {
          requestId: authSession.auth_request_id,
          authenticationMethod: "passkey",
          succeeded: false,
          failureReason: "declined"
        };
        return result;
      }
      throw error;
    }
    const webAuthnResult = await this.apiClient.completeWebAuthnAuthenticationAsync({
      authenticatorResponseData: JSON.stringify(authentication.authenticator_response_data),
      authRequestId: authentication.auth_request_id,
      authArmorSignature: authentication.aa_sig,
      webAuthnClientId: this.webAuthnClientId
    });
    const result = {
      succeeded: true,
      requestId: webAuthnResult.auth_request_id,
      authenticationMethod: "passkey",
      username,
      validationToken: webAuthnResult.auth_validation_token
    };
    return result;
  }

  /**
   * Registers a user using an authenticator QR code.
   *
   * @param username The username of the user.
   * @param options The options to use for this request.
   * @param abortSignal The abort signal to use for this request.
   *
   * @returns A promise that resolves with a QR code result for the registration result.
   */
  async registerWithAuthenticatorQrCodeAsync(username, {
    actionName = "Log in",
    shortMessage = "Registration pending, please authorize",
    timeoutSeconds = 120
  } = {}, abortSignal) {
    await this.ensureInitialized();
    const nonce = this.nonceGenerator.generateNonce();
    const registrationSession = await this.apiClient.startAuthenticatorRegistrationAsync({
      username,
      actionName,
      shortMessage,
      timeoutSeconds,
      nonce
    });
    const result = {
      qrCodeUrl: registrationSession.qr_code_data,
      verificationCode: null,
      resultAsync: async () => await this.pollForRegistrationResultAsync("authenticator", registrationSession.registration_id, username, registrationSession.registration_validation_token, timeoutSeconds, abortSignal)
    };
    return result;
  }

  /**
   * Sends a register magic link to the user's email address.
   *
   * @param emailAddress The email address of the user.
   * @param redirectUrl The URL to redirect to after the user has logged in.
   * @param options The options to use for this request.
   * @param captchaConfirmationRequest The CAPTCHA confirmation.
   *
   * @returns A promise that resolves with the registration result.
   *
   * @remarks
   * The user will be redirected to the specified URL after they have registered. The validation
   * token and registration ID will be added as query parameters with the names
   * `registration_validation_token` and `registration_id` respectively.
   *
   * The validation token will not be included in the returned `RegistrationResult`.
   */
  async sendRegisterMagicLinkEmailAsync(emailAddress, redirectUrl, {
    context = {},
    actionName = "Register",
    shortMessage = "Registration pending, please authorize",
    timeoutSeconds = 300
  } = {}, captchaConfirmationRequest, abortSignal) {
    await this.ensureInitialized();
    const nonce = this.nonceGenerator.generateNonce();
    const registrationSession = await this.apiClient.sendMagicLinkEmailForRegistrationAsync({
      username: emailAddress,
      redirectUrl,
      context,
      actionName,
      shortMessage,
      timeoutSeconds,
      nonce
    }, captchaConfirmationRequest);
    const result = await this.pollForRegistrationResultAsync("magicLinkEmail", registrationSession.registration_id, emailAddress, "", timeoutSeconds, abortSignal);
    return result;
  }

  /**
   * Registers a user using a passkey.
   *
   * @param username The username of the user.
   * @param options The options to use for this request.
   *
   * @returns A promise that resolves with the registration result.
   */
  async registerWithPasskeyAsync(username, {
    attachmentType = "any",
    residentKeyRequirementType = "required",
    userVerificationRequirementType = "required"
  } = {}) {
    if (this.webAuthnClientId === null) {
      throw new Error("This AuthArmorClient was not instantiated with passkey support because a WebAuthn client ID was not provided.");
    }
    await this.ensureInitialized();
    const nonce = this.nonceGenerator.generateNonce();
    const attachmentTypeMap = {
      any: "Any",
      crossPlatform: "CrossPlatform",
      platform: "Platform"
    };
    const registrationSession = await this.apiClient.startWebAuthnRegistrationAsync({
      username,
      attachmentType: attachmentTypeMap[attachmentType],
      residentKeyRequirementType,
      userVerificationRequirementType,
      webAuthnClientId: this.webAuthnClientId,
      nonce
    });
    let registration;
    try {
      registration = await this.webAuthnService.registerAsync(registrationSession);
    } catch {
      const result = {
        registrationId: registrationSession.registration_id,
        authenticationMethod: "passkey",
        succeeded: false,
        failureReason: "unknown"
      };
      return result;
    }
    const webAuthnResult = await this.apiClient.completeWebAuthnRegistrationAsync({
      authenticatorResponseData: registration.authenticator_response_data,
      registrationId: registration.registration_id,
      authArmorSignature: registration.aa_sig,
      webAuthnClientId: this.webAuthnClientId
    });
    const result = {
      registrationId: registrationSession.registration_id,
      authenticationMethod: "passkey",
      succeeded: true,
      username,
      validationToken: webAuthnResult.registration_validation_token
    };
    return result;
  }

  /**
   * Polls the API for the status of an authenticator authentication request.
   *
   * @param sessionId The ID of the authentication session.
   * @param validationToken The validation token for the authentication session.
   * @param timeoutSeconds The number of seconds to wait before timing out.
   * @param abortSignal The abort signal to use for this request.
   *
   * @returns A promise that resolves with the authentication result.
   */
  pollForAuthenticationResultAsync(authenticationMethod, sessionId, validationToken, timeoutSeconds = 60, abortSignal) {
    return new Promise((resolve, reject) => {
      const timesOutAfter = this.systemClock.now().getTime() + timeoutSeconds * 1000;
      const interval = setInterval(async () => {
        if (abortSignal?.aborted) {
          clearInterval(interval);
          reject(abortSignal.reason);
          return;
        }
        if (this.systemClock.now().getTime() > timesOutAfter) {
          clearInterval(interval);
          const result = {
            requestId: sessionId,
            authenticationMethod,
            succeeded: false,
            failureReason: "timedOut"
          };
          resolve(result);
          return;
        }
        const status = await this.apiClient.getAuthenticationSessionStatusAsync({
          sessionId
        });
        if (status.auth_response_code === AuthenticationRequestCode.Succeeded) {
          clearInterval(interval);
          const result = {
            requestId: sessionId,
            authenticationMethod,
            succeeded: true,
            username: status.username,
            validationToken
          };
          resolve(result);
        } else if (status.auth_request_status_id === AuthenticationRequestStatusId.Failed) {
          clearInterval(interval);
          const failureReason = this.getAuthenticationFailureReason(status.auth_response_code);
          const result = {
            requestId: sessionId,
            authenticationMethod,
            succeeded: false,
            failureReason
          };
          resolve(result);
        }
      }, 1000);
    });
  }

  /**
   * Polls the API for the status of a registration request.
   *
   * @param userId The ID of the user.
   * @param timeoutSeconds The number of seconds after which to consider the request timed out.
   * @param abortSignal The abort signal to use for this request.
   *
   * @returns A promise that resolves with the registration result.
   */
  pollForRegistrationResultAsync(authenticationMethod, registrationId, username, validationToken, timeoutSeconds = 60, abortSignal) {
    return new Promise((resolve, reject) => {
      const timesOutAfter = this.systemClock.now().getTime() + timeoutSeconds * 1000;
      const interval = setInterval(async () => {
        if (abortSignal?.aborted) {
          clearInterval(interval);
          reject(abortSignal.reason);
          return;
        }
        if (this.systemClock.now().getTime() > timesOutAfter) {
          clearInterval(interval);
          const result = {
            registrationId,
            authenticationMethod,
            succeeded: false,
            failureReason: "timedOut"
          };
          resolve(result);
          return;
        }
        const registrationSessionStatus = await this.apiClient.getRegistrationSessionStatusAsync({
          registrationId
        });
        switch (registrationSessionStatus.registration_status_code) {
          case RegistrationRequestStatusCode.PendingValidation:
          case RegistrationRequestStatusCode.Registered:
            {
              clearInterval(interval);
              const result = {
                registrationId,
                authenticationMethod,
                succeeded: true,
                username,
                validationToken
              };
              resolve(result);
              break;
            }
          case RegistrationRequestStatusCode.Declined:
            {
              clearInterval(interval);
              const result = {
                registrationId,
                authenticationMethod: "authenticator",
                succeeded: false,
                failureReason: "declined"
              };
              resolve(result);
              break;
            }
          case RegistrationRequestStatusCode.Timeout:
            {
              clearInterval(interval);
              const result = {
                registrationId,
                authenticationMethod: "authenticator",
                succeeded: false,
                failureReason: "timedOut"
              };
              resolve(result);
              break;
            }
          case RegistrationRequestStatusCode.PendingUserAcceptance:
          case RegistrationRequestStatusCode.AcceptedPendingDeviceEnrollment:
            {
              break;
            }
          default:
            {
              clearInterval(interval);
              const result = {
                registrationId,
                authenticationMethod: "authenticator",
                succeeded: false,
                failureReason: "unknown"
              };
              resolve(result);
              break;
            }
        }
      }, 1000);
    });
  }

  /**
   * Gets the failure reason for an authentication request.
   *
   * @param responseCode The response code returned by the API.
   *
   * @returns The failure reason.
   */
  getAuthenticationFailureReason(responseCode) {
    switch (responseCode) {
      case AuthenticationRequestCode.TimedOut:
        {
          return "timedOut";
        }
      case AuthenticationRequestCode.Declined:
        {
          return "declined";
        }
      default:
        {
          return "unknown";
        }
    }
  }

  /**
   * Initializes the client for making requests.
   *
   * @returns A promise that resolves when the client has been initialized
   */
  async initializeInternal() {
    const remoteSdkConfig = await this.apiClient.getSdkConfigurationAsync();
    this.hCaptchaSiteId = remoteSdkConfig.hcaptcha_site_id;
  }
}

export { ApiError, AuthArmorApiClient, AuthArmorApiRequestSigner, AuthArmorClient, AuthMethod, AuthenticationRequestCode, AuthenticationRequestStatusId, BrowserBase64Coder, BrowserNonceGenerator, BrowserSha256StringHasher, NativeSystemClock, RegistrationRequestStatusCode, WebAuthnRequestDeniedError, WebAuthnService };
